import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { MenuItem, TextField, Tooltip } from '@mui/material'
import React from 'react'
import { FilterSelect, filterSx, LookupField } from '../../../components/filter-fields'
import { listSearchSx } from '../../../components/list-styles'
import ReportBreakdown from '../../../components/ReportBreakdown'
import { ChipTabs, ReportKpi } from '../../../components/report-parts'
import ReportTable from '../../../components/ReportTable'
import type { ReportTableColumn } from '../../../components/ReportTable'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { searchClientes, searchVendedores } from '../../../utils/lookups'
import PolicyDrawer from '../../policies/drawer/policy-drawer'
import { BREAKDOWN_TABS, formatAmount, formatCount, PAGE_SIZES, RamoMark } from '../common/report-common'
import { RAMOS, useReportPoliciesModel } from './report-policies-model'
import type { PolicyReportRow } from './report-policies-model'

const dateSx = { ...filterSx, width: { xs: '100%', sm: 180 } }

const ReportPolicies = () => {
    const model = useReportPoliciesModel()
    const { filters } = model

    const columns: ReportTableColumn<PolicyReportRow>[] = [
        {
            key: 'ver',
            label: '',
            render: (row) => (
                <Tooltip title="Ver póliza">
                    <button type="button" aria-label={`Ver póliza ${row.noPoliza}`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-k-primary-ink hover:bg-k-accent-soft" onClick={() => model.openPoliza(row.id)}>
                        <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
                    </button>
                </Tooltip>
            ),
        },
        {
            key: 'noPoliza',
            label: 'Póliza',
            minWidth: 170,
            render: (row) => (
                <span className="flex items-center gap-2">
                    <RamoMark ramo={row.ramo} boxed size={14} />
                    <span className="flex flex-col">
                        <span className="font-bold text-k-ink">{row.noPoliza || '—'}</span>
                        <span className="text-xs text-k-muted">{row.ramo}</span>
                    </span>
                    {row.robot && (
                        <Tooltip title="Creada por la sincronización automática">
                            <SmartToyOutlinedIcon className="text-tone-orange-ink" style={{ fontSize: 16 }} />
                        </Tooltip>
                    )}
                </span>
            ),
        },
        {
            key: 'contratante',
            label: 'Contratante',
            minWidth: 200,
            render: (row) => (row.contratante ? (
                <Tooltip title={row.contratante} placement="top-start">
                    <span className="block max-w-[240px] truncate text-k-ink-2">{row.contratante}</span>
                </Tooltip>
            ) : <span className="text-k-muted">—</span>),
        },
        {
            key: 'concepto',
            label: 'Concepto',
            minWidth: 180,
            render: (row) => (row.concepto ? (
                <Tooltip title={row.concepto} placement="top-start">
                    <span className="block max-w-[240px] truncate text-k-ink-2">{row.concepto}</span>
                </Tooltip>
            ) : <span className="text-k-muted">—</span>),
        },
        { key: 'aseguradora', label: 'Aseguradora', render: (row) => <span className="flex flex-col"><span className="font-semibold text-k-ink">{row.aseguradora || '—'}</span><span className="text-xs text-k-muted">Clave {row.clave || '—'}</span></span> },
        { key: 'status', label: 'Estatus', render: (row) => <span className="whitespace-nowrap rounded-full bg-k-accent-soft px-[9px] py-[3px] text-xs font-bold text-k-accent-ink">{row.status}</span> },
        { key: 'formaPago', label: 'Forma de pago', render: (row) => <span className="whitespace-nowrap text-k-ink-2">{row.formaPago}</span> },
        { key: 'vigencia', label: 'Vigencia', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.vigenciaDe} – {row.vigenciaA}</span> },
        { key: 'creacion', label: 'Creación', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-muted">{row.creacion}</span> },
        { key: 'prima', label: 'Prima neta', align: 'right', render: (row) => <span className="whitespace-nowrap">${formatAmount(row.prima)} <span className="text-xs text-k-muted">{row.moneda}</span></span> },
        { key: 'total', label: 'Prima total', align: 'right', render: (row) => <span className="whitespace-nowrap font-bold text-k-ink">${formatAmount(row.total)} <span className="text-xs font-normal text-k-muted">{row.moneda}</span></span> },
        { key: 'vendedor', label: 'Vendedor', minWidth: 180, render: (row) => <span className="truncate text-k-ink-2">{row.vendedor || '—'}</span> },
    ]

    return (
        <KarteraAdminTemplate loading={false} title="Reporte de pólizas">
            <PolicyDrawer polizaId={model.detailId} onClose={model.closePoliza} />

            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Reporte de pólizas"
                    description="Pólizas por fecha de inicio de vigencia, con sus acumulados por ramo, estatus y aseguradora."
                    actions={(
                        <button
                            type="button"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={model.reload}
                        >
                            <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                            Actualizar
                        </button>
                    )}
                />

                <section className="k-card gap-3">
                    <div className="flex flex-wrap items-start gap-2.5">
                        <TextField
                            type="date" size="small" label="Vigencia desde" sx={dateSx}
                            value={filters.vigenciaDe}
                            error={model.rangeInvalid}
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(event) => model.change('vigenciaDe', event.target.value)}
                        />
                        <TextField
                            type="date" size="small" label="Vigencia hasta" sx={dateSx}
                            value={filters.vigenciaA}
                            error={model.rangeInvalid}
                            slotProps={{ inputLabel: { shrink: true } }}
                            onChange={(event) => model.change('vigenciaA', event.target.value)}
                        />
                        <FilterSelect label="Estatus" value={filters.status} onChange={(event) => model.change('status', event.target.value)}>
                            <MenuItem value=""><em>Todos</em></MenuItem>
                            {model.statuses.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                        </FilterSelect>
                        <FilterSelect label="Ramo" value={filters.ramo} onChange={(event) => model.change('ramo', event.target.value)}>
                            <MenuItem value=""><em>Todos</em></MenuItem>
                            {RAMOS.map((ramo) => <MenuItem key={ramo} value={ramo}><span className="inline-flex items-center gap-2"><RamoMark ramo={ramo} />{ramo}</span></MenuItem>)}
                        </FilterSelect>
                        <FilterSelect label="Forma de pago" value={filters.formaPago} minWidth={160} onChange={(event) => model.change('formaPago', event.target.value)}>
                            <MenuItem value=""><em>Todas</em></MenuItem>
                            {model.formas.map((item) => <MenuItem key={item.key} value={item.key}>{item.value}</MenuItem>)}
                        </FilterSelect>
                        <FilterSelect label="Moneda" value={filters.moneda} onChange={(event) => model.change('moneda', event.target.value)}>
                            <MenuItem value=""><em>Todas</em></MenuItem>
                            {model.monedas.map((item) => <MenuItem key={item.id} value={item.abreviacion || ''}>{item.abreviacion}</MenuItem>)}
                        </FilterSelect>
                        <FilterSelect label="Aseguradora" value={filters.aseguradoraId} minWidth={200} onChange={(event) => model.change('aseguradoraId', event.target.value)}>
                            <MenuItem value=""><em>Todas</em></MenuItem>
                            {model.aseguradoras.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.aseguradora} ({item.abreviacion})</MenuItem>)}
                        </FilterSelect>
                        <LookupField label="Cliente" value={filters.cliente} search={searchClientes} onChange={(cliente) => model.change('cliente', cliente)} />
                        <LookupField label="Vendedor" value={filters.vendedor} search={searchVendedores} onChange={(vendedor) => model.change('vendedor', vendedor)} />
                        <button
                            type="button"
                            title="Limpiar filtros"
                            aria-label="Limpiar filtros"
                            className="ml-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-k-danger-soft text-k-danger-ink hover:brightness-95"
                            onClick={model.clearFilters}
                        >
                            <CloseIcon style={{ fontSize: 18 }} />
                        </button>
                    </div>
                    {model.rangeError && <p className="m-0 text-xs font-semibold text-k-danger-ink">{model.rangeError}</p>}
                </section>

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <div className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${model.loading ? 'opacity-60' : ''}`}>
                    <ReportKpi label="Pólizas" value={formatCount(model.count)} />
                    <ReportKpi label="Prima total" value={model.totals.value} hint={model.totals.hint} />
                    <ReportKpi label="Prima neta" value={model.netTotals.value} hint={model.netTotals.hint} />
                    <ReportKpi label="Por sincronización" value={formatCount(model.robots)} hint="Creadas automáticamente" />
                </div>

                <div className="flex flex-col gap-3">
                    <ChipTabs label="Desglose" tabs={BREAKDOWN_TABS} value={model.dimension} onChange={model.changeDimension} />
                    <div className={model.loading ? 'opacity-60' : ''}>
                        <ReportBreakdown
                            title={`Pólizas y primas por ${BREAKDOWN_TABS.find((tab) => tab.id === model.dimension)?.label.toLowerCase()}`}
                            items={model.breakdown}
                            countLabel="Pólizas"
                            formatCount={formatCount}
                            formatSum={model.formatSum}
                            renderIcon={model.dimension === 'ramo' ? (name) => <RamoMark ramo={name} /> : undefined}
                        />
                    </div>
                </div>

                <ReportTable<PolicyReportRow>
                    columns={columns}
                    rows={model.rows}
                    rowKey={(row) => row.id}
                    emptyText={model.loading ? 'Cargando pólizas…' : 'No hay pólizas con los filtros aplicados.'}
                    loading={model.loading}
                    page={model.page}
                    totalPages={model.totalPages}
                    pages={model.pages}
                    pageSize={model.pageSize}
                    pageSizes={PAGE_SIZES}
                    rangeFrom={model.rangeFrom}
                    rangeTo={model.rangeTo}
                    totalRows={model.filteredCount}
                    noun="pólizas"
                    formatCount={formatCount}
                    onPage={model.setPage}
                    onPageSize={model.changePageSize}
                    toolbar={(
                        <>
                            <div className="min-w-0 flex-1">
                                <TextField
                                    label="Buscar en la tabla"
                                    size="small"
                                    value={model.search}
                                    sx={listSearchSx}
                                    slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                    onChange={(event) => model.changeSearch(event.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                disabled={model.filteredCount === 0}
                                onClick={model.exportCsv}
                                className="inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <DownloadOutlinedIcon style={{ fontSize: 18 }} />
                                Exportar CSV
                            </button>
                        </>
                    )}
                />
            </div>
        </KarteraAdminTemplate>
    )
}

export default ReportPolicies
